
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authorizedRequest } from '../../api/authorizedRequest';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
}

const ResetPassword: React.FC = () => {
  const { token } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'danger' | ''>('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{oldPassword?: string; newPassword?: string; confirmPassword?: string}>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    setErrors({});

    let hasError = false;
    const newErrors: {oldPassword?: string; newPassword?: string; confirmPassword?: string} = {};
    if (!oldPassword) {
      newErrors.oldPassword = 'Old password is required.';
      hasError = true;
    }
    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
      hasError = true;
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password.';
      hasError = true;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) return;

    setLoading(true);
    try {
      const decodedToken = jwtDecode<DecodedToken>(token || '');
      const username = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
      const res = await authorizedRequest({
        url: '/Auth/reset-password',
        method: 'POST',
        data: { username, oldPassword, newPassword },
      });

      if (res.status === 200) {
        setMessage('Password reset successful.');
        setMessageType('success');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const err = await res.data;
        setMessage(err?.[0]?.description || 'Password reset failed.');
        setMessageType('danger');
      }
    } catch (error) {
      setMessage('An unexpected error occurred.');
      setMessageType('danger');
    }
    setLoading(false);
  };

  return (
    <div className="container" style={{ maxWidth: 400, marginTop: 40 }}>
      <h3>Reset Password</h3>
      {message && (
        <div className={`alert alert-${messageType} mt-3`} role="alert">
          {message}
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group row">
          <label className="col-sm-12 col-md-4 col-form-label">Old Password</label>
          <div className="col-sm-12 col-md-8">
            <input
              className={`form-control${errors.oldPassword ? ' is-invalid' : ''}`}
              type="password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              required
            />
            {errors.oldPassword && <div className="invalid-feedback">{errors.oldPassword}</div>}
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-12 col-md-4 col-form-label">New Password</label>
          <div className="col-sm-12 col-md-8">
            <input
              className={`form-control${errors.newPassword ? ' is-invalid' : ''}`}
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
            />
            {errors.newPassword && <div className="invalid-feedback">{errors.newPassword}</div>}
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-12 col-md-4 col-form-label">Confirm New Password</label>
          <div className="col-sm-12 col-md-8">
            <input
              className={`form-control${errors.confirmPassword ? ' is-invalid' : ''}`}
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
          </div>
        </div>
        <button className="btn btn-secondary" type="submit" disabled={loading}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
